import React, { useState } from 'react';
import propTypes from 'prop-types';

const daysOfWeek = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
};

const loadMoreState = Object.freeze({
  EMPTY: 'empty',
  LOADING: 'loading',
  LOADED: 'loaded',
  ERROR: 'error',
});

const currentTime = () => {
  const now = new Date();
  return {
    now,
    year: now.getFullYear(),
    month: now.getMonth(),
    day: now.getDate(),
  };
};

const isDayHoliday = (holidays, date = new Date()) => {
  if (holidays == null) {
    return false;
  }

  const testYear = date.getFullYear();
  const testMonth = date.getMonth();
  const testDay = date.getDate();
  // format the date into a string to account for local timezone
  const potentialHoliday = new Date(`${testYear}-${testMonth + 1}-${testDay}`).getTime();

  // we only care if the day is a holiday if that means the store is closed
  return holidays
    .some((holiday) => new Date(holiday.date).getTime() === potentialHoliday && holiday.isClosed);
};

const getDateFormattedHours = (hours, year, month, day) => {
  const { isClosed } = hours;
  if (isClosed) {
    return {
      isClosed,
      opensAt: null,
      closesAt: null,
    };
  }

  const [opensAtHour, opensAtMinute] = hours.opensAt.split(':');
  const [closesAtHour, closesAtMinute] = hours.closesAt.split(':');

  return {
    isClosed,
    opensAt: new Date(year, month, day, opensAtHour, opensAtMinute),
    closesAt: new Date(year, month, day, closesAtHour, closesAtMinute),
  };
};

const hasOpenHours = (storeHours) => Object.keys(storeHours)
  .filter((key) => storeHours[key] != null)
  .some((key) => !storeHours[key].isClosed && storeHours[key].opensAt);

const getNextOpenTime = (storeHours, year, month, day, offset) => {
  const nextDay = new Date(year, month, day + offset);
  const dayOfWeek = nextDay.getDay();
  if (isDayHoliday(storeHours.holidays, nextDay)) {
    return getNextOpenTime(storeHours, year, month, day, offset + 1);
  }

  const {
    isClosed,
    opensAt,
  } = getDateFormattedHours(storeHours[daysOfWeek[dayOfWeek]], year, month, day);

  if (!isClosed && opensAt) {
    return {
      dayOfWeek: daysOfWeek[dayOfWeek],
      opensAt: opensAt.toLocaleTimeString('en-US', { timeStyle: 'short' }),
    };
  }

  return getNextOpenTime(storeHours, year, month, day, offset + 1);
};

const DashSeparator = () => (
  <>
    <span className="cmp-store-card__dash-separator">
      {' - '}
    </span>
    <br className="cmp-store-card__line-break" />
  </>
);

const displayStoreClosedForToday = (storeHours, year, month, day) => {
  // in case the store does not have open hours on any day, abort
  if (!hasOpenHours(storeHours)) {
    return (
      <p>
        <span className="util-weight-medium">Closed now</span>
        <DashSeparator />
        Contact Store for Hours
      </p>
    );
  }

  const nextOpenTime = getNextOpenTime(storeHours, year, month, day, 1);
  return (
    <p>
      <span className="util-weight-medium">Closed now</span>
      <DashSeparator />
      <span>
        {'Opens on '}
        <span className="util-text-capitalize">{nextOpenTime.dayOfWeek}</span>
        {' at '}
        {nextOpenTime.opensAt}
      </span>
    </p>
  );
};

const StoreHoursInfo = ({ storeHours }) => {
  const {
    now, year, month, day,
  } = currentTime();
  if (isDayHoliday(storeHours.holidays, now)) {
    return displayStoreClosedForToday(storeHours, year, month, day);
  }

  const dayOfWeek = daysOfWeek[now.getDay()];
  const {
    isClosed,
    opensAt,
    closesAt,
  } = getDateFormattedHours(storeHours[dayOfWeek], year, month, day);

  if (isClosed || now >= closesAt) {
    return displayStoreClosedForToday(storeHours, year, month, day);
  }

  if (now < opensAt) {
    return (
      <p>
        <span className="util-weight-medium">Closed now</span>
        <DashSeparator />
        {'Opens at '}
        {opensAt.toLocaleTimeString('en-US', { timeStyle: 'short' })}
      </p>
    );
  }

  return (
    <p>
      <span className="util-weight-medium">Open now</span>
      <DashSeparator />
      {'Closes at '}
      {closesAt.toLocaleTimeString('en-US', { timeStyle: 'short' })}
    </p>
  );
};

const StoreList = ({
  stores,
  zipCode,
  handleStoreSelection,
  loadMore,
}) => {
  const [loadingState, setLoadingState] = useState(loadMoreState.EMPTY);

  const handleLoadMore = async (evt) => {
    evt.preventDefault();
    evt.stopPropagation();

    if (loadMore) {
      setLoadingState(loadMoreState.LOADING);
      await loadMore();
      setLoadingState(loadMoreState.LOADED);
    }

    return false;
  };

  if (!stores.length) {
    return (
      <div className="cmp-store-card__list modal__overflow-section">
        <p className="cmp-store-card__store-count">
          {`No stores found near "${zipCode}." `}
          View all stores to find a store.
        </p>
        <div>
          <a href={`${window.drupalSettings.locationURL}/search`} className="cmp-cta-link">
            <span>
              View All Stores
            </span>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="cmp-store-card__list modal__overflow-section">
      <p className="cmp-store-card__store-count">
        {`${stores.length} ${stores.length === 1 ? ' store' : ' stores'} near "${zipCode}"`}
      </p>
      {stores.map((store) => (
        <article className="cmp-store-card" key={store.storeId} data-testid={store.storeId}>
          <h4 className="cmp-store-card__heading">{store.name}</h4>
          <div className="cmp-store-card__hours">
            <StoreHoursInfo storeHours={store.storeHours} />
          </div>
          <div className="cmp-store-card__address">
            <p>{store.addressLine1}</p>
            <p>
              {store.city}
              {', '}
              {store.state}
              {' '}
              {store.zip}
              {' '}
              {store.country !== 'US' ? `, ${store.country}` : ''}
            </p>
          </div>
          <p className="cmp-store-card__phone">
            <a href={`tel:${store.phone}`} aria-label={store.phoneAccessible}>{store.phoneVisual}</a>
          </p>
          <div className="cmp-store-card__button-wrapper">
            <button
              type="button"
              className="cmp-cta-link cmp-cta-link--inverted"
              aria-label={`Choose this store: ${store.name}`}
              onClick={() => handleStoreSelection(store)}
              data-testid={`${store.storeId}-button`}
            >
              <span>
                Choose
                <span className="util-display-none util-display-inline@md">
                  {' This Store'}
                </span>
              </span>
            </button>
          </div>
        </article>
      ))}
      <a href="#0" onClick={handleLoadMore} className="cmp-cta-link">
        <span>
          {loadingState === loadMoreState.LOADING && 'Loading...'}
          {(
            loadingState === loadMoreState.LOADED
            || loadingState === loadMoreState.EMPTY
          )
          && 'Load More'}
        </span>
      </a>
    </div>
  );
};

const storeHoursPropTypes = propTypes.shape({
  isClosed: propTypes.bool,
  opensAt: propTypes.string,
  closesAt: propTypes.string,
});

StoreHoursInfo.propTypes = {
  storeHours: propTypes.shape({
    monday: storeHoursPropTypes,
    tuesday: storeHoursPropTypes,
    wednesday: storeHoursPropTypes,
    thursday: storeHoursPropTypes,
    friday: storeHoursPropTypes,
    saturday: storeHoursPropTypes,
    sunday: storeHoursPropTypes,
    holidays: propTypes.arrayOf(propTypes.shape({
      date: propTypes.string,
      isClosed: propTypes.bool,
    })),
  }),
};

StoreList.propTypes = {
  stores: propTypes.arrayOf(propTypes.shape({
    storeId: propTypes.string,
    market: propTypes.string,
    name: propTypes.string,
    addressLine1: propTypes.string,
    city: propTypes.string,
    state: propTypes.string,
    zip: propTypes.string,
    country: propTypes.string,
    lat: propTypes.number,
    lng: propTypes.number,
    storeLink: propTypes.string,
    phone: propTypes.string,
    phoneVisual: propTypes.string,
    phoneAccessible: propTypes.string,
    distanceInMiles: propTypes.number,
    storeHours: StoreHoursInfo.propTypes.storeHours,
  })),
  zipCode: propTypes.string,
  handleStoreSelection: propTypes.func,
  loadMore: propTypes.func,
};

export { StoreList };
